/**
 * Reg D 506(b) investor questionnaire - the screen only US persons (residents of the United
 * States) ever see, shown at the moment they try to invest (never at onboarding, never to a
 * Mexican resident). The answer is kept as the person's profile and pre-fills the questionnaire
 * they confirm at the signature of each contract: the status that counts is the one of the
 * subscription (D-14 B). "None of these" is an answer: a non-accredited person is admitted.
 */
import { useEffect, useState } from 'react';

import accreditationService, {
  type AccreditationCategory,
} from '../../services/accreditation.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';
import { useMovablePopup } from '../MovablePopup';
import { ACCREDITATION_AFFIRMATIONS } from './accreditationAffirmations';

interface Props {
  onCertified: () => void;
  onClose: () => void;
}

export default function AccreditationModal({ onCertified, onClose }: Readonly<Props>): JSX.Element {
  const popup = useMovablePopup<HTMLDialogElement>();
  const [categories, setCategories] = useState<AccreditationCategory[]>([]);
  const [category, setCategory] = useState<string | null>(null);
  const [informationTrue, setInformationTrue] = useState(false);
  const [understandsRisk, setUnderstandsRisk] = useState(false);
  const [willNotifyChanges, setWillNotifyChanges] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    accreditationService
      .getStatus()
      .then((s) => setCategories(s.categories))
      .catch((e: unknown) =>
        setError(getApiErrorMessage(e, "Couldn't load the certification form. Please try again.")),
      );
  }, []);

  const canSubmit =
    !!category && informationTrue && understandsRisk && willNotifyChanges && !busy;

  async function handleSubmit(): Promise<void> {
    if (!category || !canSubmit) return;
    setError(null);
    setBusy(true);
    try {
      await accreditationService.certify(category, {
        informationTrue,
        understandsRisk,
        willNotifyChanges,
      });
      onCertified();
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, "Couldn't record your certification. Please try again."));
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(10, 16, 32, 0.6)' }}
    >
      <dialog
        ref={popup.panelRef}
        open
        aria-label="Investor accreditation"
        className="w-full max-w-lg rounded-2xl p-6 overflow-y-auto"
        style={{
          ...popup.panelStyle,
          position: 'static',
          margin: 0,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          maxHeight: '90vh',
        }}
      >
        {popup.bar}
        <h2 className="text-lg font-bold mb-1" style={{ color: colors.ink }}>
          Investor accreditation
        </h2>
        <p className="text-xs mb-4" style={{ color: colors.muted }}>
          Required by U.S. federal law (Regulation D) before contributing to this series. Your answer is kept in your profile and fills in the questionnaire you confirm again each time you contribute.
        </p>

        <p className="text-sm font-semibold mb-2" style={{ color: colors.ink }}>
          Which of these applies to you?
        </p>
        <div className="mb-4">
          {categories.map((c) => (
            <label
              key={c.key}
              className="flex items-start gap-2 mb-2 text-sm"
              style={{ color: colors.ink, cursor: 'pointer' }}
            >
              <input
                type="radio"
                name="accreditation-category"
                checked={category === c.key}
                onChange={() => setCategory(c.key)}
                style={{ marginTop: 3 }}
              />
              <span>{c.statement}</span>
            </label>
          ))}
        </div>

        <div className="mb-4">
          {[
            { checked: informationTrue, set: setInformationTrue, text: ACCREDITATION_AFFIRMATIONS[0] },
            { checked: understandsRisk, set: setUnderstandsRisk, text: ACCREDITATION_AFFIRMATIONS[1] },
            {
              checked: willNotifyChanges,
              set: setWillNotifyChanges,
              text: ACCREDITATION_AFFIRMATIONS[2],
            },
          ].map((a) => (
            <label
              key={a.text}
              className="flex items-start gap-2 mb-2 text-xs"
              style={{ color: colors.inkSoft, cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={a.checked}
                onChange={(e) => a.set(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span>{a.text}</span>
            </label>
          ))}
        </div>

        {error && (
          <p className="text-xs mb-3" style={{ color: colors.danger }} role="alert">
            {error}
          </p>
        )}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-sm font-semibold"
            style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}
          >
            Not now
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="text-sm font-semibold px-4 py-2 rounded-xl"
            style={{
              background: canSubmit ? colors.navy : colors.border,
              color: '#FFFFFF',
              border: 'none',
              cursor: canSubmit ? 'pointer' : 'default',
            }}
          >
            {busy ? 'Submitting…' : 'Certify and continue'}
          </button>
        </div>
      </dialog>
    </div>
  );
}
